import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { accountability, assignments, formFields } from "../utils/api";
import { useToast } from "../contexts/ToastContext";
import ShareModal from "../components/ShareModal";

const SYSTEM_COLUMNS = {
  wakeupTime: "wakeup_time",
  mangalaAarti: "mangala_aarti",
  morningKatha: "morning_katha",
  morningPujaTime: "morning_puja_time",
  meditationWatchTime: "meditation_watch_time",
  vachanamrutRead: "vachanamrut_read",
  cheshta: "cheshta",
  mansiPujaCount: "mansi_puja_count",
  readingTime: "reading_time",
  wastedTime: "wasted_time",
  mantraJap: "mantra_jap",
  notes: "notes",
};

const parseJson = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

const durationToMinutes = (value) => {
  if (!value || typeof value !== "string") return "";
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

const defaultValueForField = (field) => {
  if (field.field_type === "yes_no") return false;
  if (field.field_type === "select" && field.system_key === "morningKatha") {
    return field.options?.[0]?.value || "";
  }
  return "";
};

const valuesFromEntry = (fields, entry) => {
  const customValues = parseJson(entry?.custom_values);
  return fields.reduce((values, field) => {
    let value;
    if (field.system_key) {
      value = entry?.[SYSTEM_COLUMNS[field.system_key]];
      if (field.field_type === "duration") value = durationToMinutes(value);
    } else {
      value = customValues[field.field_key];
    }

    if (value === undefined || value === null) {
      value = defaultValueForField(field);
    } else if (field.field_type === "yes_no") {
      value = Boolean(value);
    }
    values[field.field_key] = value;
    return values;
  }, {});
};

const DailyFormPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { entryData, readOnly, targetDate, targetUserId } = location.state || {};
  const isReadOnly =
    readOnly || user?.role === "admin" || (targetUserId && targetUserId !== user?.id);

  const initialDate = entryData?.entry_date || targetDate || new Date().toISOString().split("T")[0];
  const [entryDate, setEntryDate] = useState(initialDate);
  const [currentEntryDate, setCurrentEntryDate] = useState(initialDate);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [formLoading, setFormLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(targetUserId || user?.id);
  const [showShareModal, setShowShareModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await formFields.list();
        setFields(response.data);
        setFieldValues(valuesFromEntry(response.data, entryData));
      } catch (error) {
        showError("Failed to load the daily form");
      } finally {
        setFormLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!user?.is_monitor) return;
    assignments
      .getAssignedStudents(user.id)
      .then((response) => setAssignedStudents(response.data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (entryData || fields.length === 0 || !currentEntryDate || !selectedStudentId) return;
    const fetchExistingEntry = async () => {
      try {
        const response = await accountability.list({
          date: currentEntryDate,
          userId: selectedStudentId,
        });
        setFieldValues(valuesFromEntry(fields, response.data[0]));
      } catch (error) {
        showError("Failed to check for an existing entry");
      }
    };
    fetchExistingEntry();
  }, [currentEntryDate, selectedStudentId, entryData, fields]);

  const updateValue = (fieldKey, value) => {
    setFieldValues((current) => ({ ...current, [fieldKey]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        userId: selectedStudentId,
        entryDate,
        fieldValues,
      };
      await accountability.create(payload);
      const shareData = { ...payload, fields };
      setSubmittedData(shareData);
      showSuccess("Entry saved successfully!");
      if (Number(selectedStudentId) === Number(user?.id)) {
        setShowShareModal(true);
      } else {
        setTimeout(() => navigate("/dashboard"), 800);
      }
    } catch (error) {
      const details = error.response?.data?.details;
      showError(details?.[0] || error.response?.data?.error || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const formatShareValue = (field, value) => {
    if (value === "" || value === undefined || value === null) return "-";
    if (field.field_type === "yes_no") return value ? "Yes" : "No";
    if (field.field_type === "duration") return `${value} minutes`;
    if (field.field_type === "select") {
      return field.options?.find((option) => String(option.value) === String(value))?.label || value;
    }
    if (field.field_type === "date") {
      return new Date(`${value}T00:00:00`).toLocaleDateString("en-GB");
    }
    if (field.field_type === "time") {
      return new Date(`2000-01-01T${value}`).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return String(value);
  };

  const handleShare = async () => {
    if (!submittedData) return;
    const reportLines = fields.map(
      (field) => `${field.label}: ${formatShareValue(field, submittedData.fieldValues[field.field_key])}`
    );
    const message = `🫂 ધામભાવે અખંડ સ્મરણ વર્ષ ના જય સ્વામિનારાયણ 🫂\n\nDaily Report\n\nDate: ${new Date(
      `${submittedData.entryDate}T00:00:00`
    ).toLocaleDateString("en-GB")}\n\n${reportLines.join("\n\n")}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: "Daily Accountability Report", text: message });
      } else {
        await navigator.clipboard.writeText(message);
        showSuccess("Report copied to clipboard!");
      }
    } catch (error) {
      if (error.name !== "AbortError") showError("Failed to share report");
    } finally {
      setShowShareModal(false);
      navigate("/dashboard");
    }
  };

  const handleNotNow = () => {
    setShowShareModal(false);
    navigate("/dashboard");
  };

  const inputClass =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100";

  const renderField = (field) => {
    const value = fieldValues[field.field_key] ?? defaultValueForField(field);
    if (field.field_type === "yes_no") {
      return (
        <div className="grid grid-cols-2 gap-2" role="group" aria-label={field.label}>
          {[true, false].map((choice) => (
            <button
              key={String(choice)}
              type="button"
              disabled={isReadOnly}
              onClick={() => updateValue(field.field_key, choice)}
              className={`rounded-lg border px-4 py-3 font-medium ${
                value === choice
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-gray-300 bg-white text-gray-700"
              } disabled:cursor-default`}
            >
              {choice ? "Yes" : "No"}
            </button>
          ))}
        </div>
      );
    }

    if (field.field_type === "select") {
      return (
        <select
          required={field.is_required}
          disabled={isReadOnly}
          value={value}
          onChange={(event) => updateValue(field.field_key, event.target.value)}
          className={inputClass}
        >
          <option value="" disabled={field.is_required}>Select an option</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }

    if (field.field_type === "textarea") {
      return (
        <textarea
          rows="3"
          required={field.is_required}
          disabled={isReadOnly}
          maxLength={field.max_length || 1000}
          value={value}
          onChange={(event) => updateValue(field.field_key, event.target.value)}
          className={inputClass}
          placeholder="Enter your answer"
        />
      );
    }

    const isNumber = field.field_type === "positive_integer" || field.field_type === "duration";
    return (
      <div>
        <input
          type={
            isNumber
              ? "number"
              : ["date", "time"].includes(field.field_type)
              ? field.field_type
              : "text"
          }
          required={field.is_required}
          disabled={isReadOnly}
          min={isNumber ? field.min_value ?? 0 : undefined}
          max={isNumber ? field.max_value ?? undefined : undefined}
          step={isNumber ? 1 : undefined}
          maxLength={field.field_type === "text" ? field.max_length || 255 : undefined}
          value={value}
          onChange={(event) => updateValue(field.field_key, event.target.value)}
          className={inputClass}
          placeholder={field.field_type === "duration" ? "Minutes" : "Enter your answer"}
        />
        {field.field_type === "duration" && (
          <p className="mt-1 text-xs text-gray-500">Enter the total number of minutes.</p>
        )}
      </div>
    );
  };

  if (formLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading daily form...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 pb-10 pt-20">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isReadOnly ? "View Daily Entry" : "Daily Accountability Form"}
          </h1>
          {entryData && (
            <p className="mt-1 text-sm text-gray-600">
              {entryData.first_name} {entryData.last_name} · {entryDate}
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {user?.is_monitor && assignedStudents.length > 0 && !isReadOnly && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fill Form For</label>
                <select
                  value={selectedStudentId || ""}
                  onChange={(event) => {
                    const nextId = event.target.value || user.id;
                    setSelectedStudentId(nextId);
                    setFieldValues(valuesFromEntry(fields, null));
                  }}
                  className={inputClass}
                >
                  <option value={user.id}>Myself ({user.firstName} {user.lastName})</option>
                  {assignedStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.first_name} {student.last_name} - Room {student.room_number}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Report Date <span className="text-red-600">*</span>
              </label>
              <input
                type="date"
                required
                disabled={isReadOnly}
                value={entryDate}
                onChange={(event) => {
                  setEntryDate(event.target.value);
                  setCurrentEntryDate(event.target.value);
                }}
                className={inputClass}
              />
            </div>

            {fields.map((field) => (
              <div key={field.id}>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {field.label}
                  {field.is_required && <span className="text-red-600"> *</span>}
                </label>
                {renderField(field)}
                {field.help_text && <p className="mt-1 text-xs text-gray-500">{field.help_text}</p>}
              </div>
            ))}

            {fields.length === 0 && (
              <div className="rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800">
                No daily questions are currently enabled. You can still save the report date.
              </div>
            )}

            {!isReadOnly && (
              <button
                type="submit"
                disabled={saving}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Entry"}
              </button>
            )}
          </form>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onShare={handleShare}
        onNotNow={handleNotNow}
        formData={submittedData || {}}
      />
    </div>
  );
};

export default DailyFormPage;
